import { useState, useEffect, useCallback, useMemo } from 'react';

import { supabase } from '../../../lib/supabase';
import { EnhancedScoringService } from '../../../shared/services/enhancedScoringService';
import { EnhancedQuestion, UserAnswer } from '../../../types/questionFormats';
import { getRawCorrectCount } from '../utils/multiplayerScoring';

interface LocalMultiplayerQuizProps {
  sessionId: string;
  userId: string;
  playerName: string;
  initialQuestions: EnhancedQuestion[];
  overallGameTimeSeconds?: number;
  roomId?: string; // Add roomId as optional
}

interface LocalMultiplayerQuizReturn {
  questions: EnhancedQuestion[];
  currentQuestion: EnhancedQuestion | undefined;
  currentQuestionIndex: number;
  score: number;
  correctCount: number;
  answers: Record<string, string>;
  gameTimer: number;
  isGameFinished: boolean;
  isSubmittingResults: boolean;
  error: string | null;
  isInitialized: boolean;
  selectAnswer: (questionId: string, selectedOption: string | UserAnswer) => void;
  handleNextQuestion: () => void;
  initializeQuestions: (questions: EnhancedQuestion[]) => void;
  submitResultsToServer: () => Promise<void>;
}

export const useLocalMultiplayerQuiz = ({
  sessionId = '',
  userId = '',
  playerName = '',
  initialQuestions = [],
  overallGameTimeSeconds = 300,
  roomId = undefined,
}: Partial<LocalMultiplayerQuizProps>): LocalMultiplayerQuizReturn => {
  const [quizState, setQuizState] = useState<{
    currentQuestionIndex: number;
    answers: Record<string, string>;
    hasAnsweredCurrentQuestion: boolean;
    score: number;
    isGameFinished: boolean;
  }>({
    currentQuestionIndex: 0,
    answers: {},
    hasAnsweredCurrentQuestion: false,
    score: 0,
    isGameFinished: false,
  });

  // Ensure the hook always returns a LocalMultiplayerQuizReturn object and never returns undefined.
  const [questions, setQuestions] = useState<EnhancedQuestion[]>(initialQuestions);
  const [gameTimer, setGameTimer] = useState(overallGameTimeSeconds);
  const [isSubmittingResults, setIsSubmittingResults] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const currentQuestion = useMemo(
    () => questions[quizState.currentQuestionIndex],
    [questions, quizState.currentQuestionIndex]
  );
  const correctCount = useMemo(
    () => getRawCorrectCount(questions, quizState.answers),
    [questions, quizState.answers],
  );

  useEffect(() => {
    setQuizState(prev => ({ ...prev, hasAnsweredCurrentQuestion: false }));
  }, [quizState.currentQuestionIndex]);

  useEffect(() => {
    if (quizState.isGameFinished) return; // Guard: do not reset if already finished
    if (initialQuestions && initialQuestions.length > 0) {
      setQuestions(prevQs => {
        const isSame = prevQs.length === initialQuestions.length && prevQs.every((q, i) => q.id === initialQuestions[i].id);
        if (isSame) return prevQs;
        setQuizState({
          currentQuestionIndex: 0,
          answers: {},
          hasAnsweredCurrentQuestion: false,
          score: 0,
          isGameFinished: false,
        });
        setGameTimer(overallGameTimeSeconds);
        setIsInitialized(true);
        return initialQuestions;
      });
    }
  }, [initialQuestions, quizState.isGameFinished]);

  const initializeQuestions = useCallback((newQuestions: EnhancedQuestion[]) => {
    if (quizState.isGameFinished) return; // Guard: do not reset if already finished
    setQuestions(newQuestions);
    setQuizState({
      currentQuestionIndex: 0,
      answers: {},
      hasAnsweredCurrentQuestion: false,
      score: 0,
      isGameFinished: false,
    });
    setGameTimer(overallGameTimeSeconds);
    setIsInitialized(true);
  }, [overallGameTimeSeconds, quizState.isGameFinished]);

  const selectAnswer = useCallback((questionId: string, selectedOption: string | UserAnswer) => {
    if (quizState.hasAnsweredCurrentQuestion) return;
    
    const currentQuestion = questions[quizState.currentQuestionIndex];
    let score = 0;
    let answerString = '';
    
    if (typeof selectedOption === 'string') {
      // Legacy string answer - simple comparison for backward compatibility
      const isCorrect = currentQuestion?.answer === selectedOption;
      score = isCorrect ? 1 : 0;
      answerString = selectedOption;
    } else {
      // Enhanced UserAnswer with potential partial credit
      if (currentQuestion && 'question_type' in currentQuestion && 'format_data' in currentQuestion) {
        // This is an enhanced question, use proper scoring
        const questionScore = EnhancedScoringService.calculateQuestionScore(
          currentQuestion as unknown as EnhancedQuestion,
          selectedOption,
          30 // Default time spent
        );
        score = questionScore.score;
        
        // Convert UserAnswer to string for storage
        switch (selectedOption.format) {
          case 'mcq_single':
            answerString = selectedOption.answer;
            break;
          case 'true_false':
            answerString = selectedOption.answer.toString();
            break;
          case 'mcq_multiple':
            answerString = JSON.stringify(selectedOption.answers);
            break;
          case 'fill_blank':
            answerString = JSON.stringify(selectedOption.answers);
            break;
          case 'ordering':
            answerString = JSON.stringify(selectedOption.order);
            break;
          default:
            answerString = JSON.stringify(selectedOption);
        }
      } else {
        // Fallback for legacy questions
        score = 0;
        answerString = JSON.stringify(selectedOption);
      }
    }
    
    const newAnswers = { ...quizState.answers, [questionId]: answerString };
    const isLastQuestion = quizState.currentQuestionIndex === questions.length - 1;

    console.log('[LocalMultiplayer] Answer selected', {
      userId,
      sessionId,
      currentQuestionIndex: quizState.currentQuestionIndex,
      questionId,
      selectedOption: typeof selectedOption === 'string' ? selectedOption : selectedOption.format,
      score,
      isLastQuestion,
      source: 'selectAnswer',
    });

    setQuizState(prev => ({
      ...prev,
      answers: newAnswers,
      hasAnsweredCurrentQuestion: true,
      score: prev.score + score, // Add the calculated score (could be partial)
      isGameFinished: isLastQuestion ? true : prev.isGameFinished,
    }));

    if (isLastQuestion) {
      console.log('[LocalMultiplayer] Last question answered, marking game as finished', {
        userId,
        sessionId,
        currentQuestionIndex: quizState.currentQuestionIndex,
        finalScore: quizState.score + score,
        source: 'selectAnswer/finish',
      });
    }
  }, [quizState, questions, userId, sessionId]);

  const handleNextQuestion = useCallback(() => {
    if (!quizState.hasAnsweredCurrentQuestion) return;
    const nextIndex = quizState.currentQuestionIndex + 1;
    const finished = nextIndex >= questions.length;
    console.log('[LocalMultiplayer] handleNextQuestion called', {
      userId,
      sessionId,
      fromIndex: quizState.currentQuestionIndex,
      toIndex: finished ? quizState.currentQuestionIndex : nextIndex,
      finished,
      source: 'handleNextQuestion',
    });
    setQuizState(prev => ({
      ...prev,
      currentQuestionIndex: finished ? prev.currentQuestionIndex : nextIndex,
      hasAnsweredCurrentQuestion: false,
      isGameFinished: finished ? true : prev.isGameFinished,
    }));
  }, [quizState, questions.length, userId, sessionId]);

  useEffect(() => {
    if (quizState.isGameFinished || !isInitialized || questions.length === 0) return;
    const timer = setInterval(() => {
      setGameTimer(prev => {
        if (prev <= 1) {
          setQuizState(state => ({ ...state, isGameFinished: true }));
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [quizState.isGameFinished, isInitialized, questions.length]);

  const submitResultsToServer = useCallback(async (): Promise<void> => {
    if (!sessionId || !userId) return;
    if (isSubmittingResults || hasSubmitted) return;

    setError(null);
    setIsSubmittingResults(true);
    try {
      let correctAnswers = 0;
      correctAnswers = getRawCorrectCount(questions, quizState.answers);
      // Store raw correct count as score for accuracy UI
      const points = correctAnswers * 10;
      const timeTaken = overallGameTimeSeconds - gameTimer;
      const score = correctAnswers; // Use this for DB submission

      console.log('[LocalMultiplayer] Submitting results to server', {
        userId,
        sessionId,
        points,
        timeTaken,
        answers: quizState.answers,
        source: 'submitResultsToServer',
      });

      const { error: submissionError } = await supabase.rpc('rpc_submit_player_results', {
        p_session_id: sessionId,
        p_user_id: userId,
        p_player_name: playerName,
        p_score: score, // Submit raw correct count for score
        p_time_taken_seconds: timeTaken,
      });
      if (submissionError) throw submissionError;

      // Upsert player's answers to multiplayer_game_room_answers
      try {
        if (!roomId) {
          console.error('[LocalMultiplayer] Cannot upsert answers: roomId is missing!', { userId, sessionId, roomId });
        } else {
          const payload = {
            room_id: roomId,
            user_id: userId,
            answers: quizState.answers,
            updated_at: new Date().toISOString(),
          };
          const { data: upsertData, error: answersError } = await supabase
            .from('multiplayer_game_room_answers')
            .upsert([payload], { onConflict: 'room_id,user_id' });
          if (answersError) {
            console.error('[LocalMultiplayer] Error upserting answers:', answersError);
          } else {
            console.log('[LocalMultiplayer] Answers upserted to multiplayer_game_room_answers', {
              userId,
              sessionId,
              answers: quizState.answers,
              upsertData,
            });
          }
        }
      } catch (err) {
        console.error('[LocalMultiplayer] Exception upserting answers:', err);
      }
      setHasSubmitted(true);
      console.log('[LocalMultiplayer] Results successfully submitted', {
        userId,
        sessionId,
        points,
        timeTaken,
        source: 'submitResultsToServer/success',
      });
    } catch (error) {
      setError('Failed to submit results');
      if (__DEV__) console.error('[LocalMultiplayer] Error submitting results:', error, {
        userId,
        sessionId,
        source: 'submitResultsToServer/error',
      });
    } finally {
      setIsSubmittingResults(false);
    }
  }, [sessionId, userId, playerName, questions, quizState.answers, overallGameTimeSeconds, gameTimer, isSubmittingResults, hasSubmitted]);

  useEffect(() => {
    if (quizState.isGameFinished && !hasSubmitted) {
      submitResultsToServer();
    }
  }, [quizState.isGameFinished, submitResultsToServer, hasSubmitted]);

  return {
    questions,
    currentQuestion,
    currentQuestionIndex: quizState.currentQuestionIndex,
    score: quizState.score,
    correctCount,
    answers: quizState.answers,
    gameTimer,
    isGameFinished: quizState.isGameFinished,
    isSubmittingResults,
    error,
    isInitialized,
    selectAnswer,
    handleNextQuestion,
    initializeQuestions,
    submitResultsToServer,
  };
}
